const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/components/ResponsiveDialog/ResponsiveDialog.tsx';
import { useTheme } from '@mui/material/styles';
import { css, cx } from '@emotion/css';
import { Dialog, DialogTitle, IconButton, useMediaQuery } from '@mui/material';
import { ReactElement, ReactNode, useEffect, useMemo, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { TextInput, useText } from '../../utils/intlUtils';

export const RESPONSIVE_DIALOG_DEFAULT_MIN_MARGIN = 16;

const getMaxWidth = (minMargin: number) => {
    return `calc(100vw - ${minMargin * 2}px)`;
};

const getMaxHeight = (minMargin: number) => {
    return `calc(100vh - ${minMargin * 2}px)`;
};

export type ResponsiveDialogSizeSettings = {
    height: string;
    minMargin: number;
    maxHeight: string;
    maxWidth: string;
    width: string;
};

function useCSSClasses(settings: ResponsiveDialogSizeSettings) {
    const { minMargin, maxWidth, width, height, maxHeight } = settings;
    const theme = useTheme();
    return useMemo(
        () => ({
            responsiveDialogCSS: css`
                .MuiDialog-paper[role='dialog'] {
                    position: absolute;

                    height: ${height};
                    max-height: min(${maxHeight}, ${getMaxHeight(minMargin)});
                    width: ${width};
                    max-width: min(${maxWidth}, ${getMaxWidth(minMargin)});

                    margin: 0;

                    .MuiDialogTitle-root {
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        font-size: 1.125rem;
                        line-height: 1.14;
                        height: ${theme.spacing(7)};
                        padding: 0 ${theme.spacing(2)};
                        gap: ${theme.spacing(2)};

                        & > .MuiIconButton-root {
                            // close button
                            flex: none;
                            padding: 0;
                        }
                    }

                    .MuiDialogContent-root {
                        position: relative;
                        padding: 0;
                    }
                }

                &.fullScreen {
                    .MuiDialog-paper[role='dialog'] {
                        position: relative;
                        top: unset;
                        bottom: unset;
                        margin: unset;

                        max-height: 100%;
                        max-width: 100%;
                        width: 100%;
                        height: 100%;

                        border-radius: 0;
                    }
                    /*
                    commented out because code should be correct, but do now want to accidentally negatively influence other products/uses

                    .MuiDialogActions-root {
                        flex: none;

                        position: relative; // This is required to make sure the footer and its shadow render over certain content.
                        padding: ${theme.spacing(1)} ${theme.spacing(2)} ${theme.spacing(3)} ${theme.spacing(2)};
                        background-color: ${theme.palette.common.white};
                        box-shadow:
                            rgba(0, 0, 0, 0.117647) 0 -1px 6px,
                            rgba(0, 0, 0, 0.117647) 0 -1px 4px;

                        display: flex;
                    }
                     */
                    .MuiDialogActions-root {
                        display: flex;
                        align-items: flex-start;
                        padding: ${theme.spacing(1)} ${theme.spacing(3)} 0;
                        height: ${theme.spacing(10)};
                        border-top: 1px solid ${theme.palette.divider};
                    }
                }
            `,
        }),
        [theme, minMargin, maxWidth, width, height, maxHeight]
    );
}

interface ResponsiveDialogHeaderProps {
    onClose?: () => void;
    title: TextInput;
}

export const ResponsiveDialogHeader = (props: ResponsiveDialogHeaderProps) => {
    const title = useText(props.title);

    const closeButton = props.onClose ? (
        <IconButton className="DialogTitle-close-button" color="inherit" onClick={props.onClose}>
            <CloseIcon />
        </IconButton>
    ) : null;

    return (
        <DialogTitle className="ResponsiveDialogHeader DialogTitle">
            <div className="DialogTitle-text">{title}</div>
            {closeButton}
        </DialogTitle>
    );
};

export interface ResponsiveDialogProps {
    allowClosing?: boolean; // Disabling closing might not cover the situation that someone can tap the background if the dialog is not full screen
    children?: ReactNode;
    className?: string;
    fullScreen?: string | boolean;
    backdropBlur?: boolean;
    header?: ReactElement<any>;
    onClose?: () => void;
    open?: boolean;
    sizeSettings?: Partial<ResponsiveDialogSizeSettings>;
    title?: TextInput;
}

export const ResponsiveDialog = (props: ResponsiveDialogProps) => {
    const { header, allowClosing = true, backdropBlur = false } = props;
    const theme = useTheme();

    const openProp = props.open ?? true; // Not ideal, but necessary for now until ResponsiveDialog is no longer in App.tsx / is without action

    const [isOpen, setIsOpen] = useState(openProp);

    useEffect(() => {
        setIsOpen(openProp);
    }, [openProp]);

    const style = props.sizeSettings ?? {};

    const minMargin = style.minMargin ?? RESPONSIVE_DIALOG_DEFAULT_MIN_MARGIN;

    const height = style.height ?? 'auto';
    const maxHeight = style.maxHeight ?? getMaxHeight(minMargin);

    const width = style.width ?? 'auto';
    const maxWidth = style.maxWidth ?? getMaxWidth(minMargin);

    const blurredBackdropSlotProps = {
        backdrop: {
            sx: {
                backgroundColor: 'rgba(0, 0, 0, 0.3)',
                backdropFilter: 'blur(20px) saturate(150%)',
                WebkitBackdropFilter: 'blur(20px) saturate(150%)',
            },
        },
    };

    const classes = useCSSClasses({
        height,
        maxHeight,
        maxWidth,
        minMargin,
        width,
    });

    const fullHeightReg = /^100(%|vh)$/;
    const fullWidthReg = /^100(%|vw)$/;
    const maxSize =
        !!style.height?.match(fullHeightReg) &&
        !!style.width?.match(fullWidthReg) &&
        (!style.maxHeight || !!style.maxHeight.match(fullHeightReg)) &&
        (!style.maxWidth || !!style.maxWidth.match(fullWidthReg));

    const fullScreen = maxSize || (props.fullScreen ?? theme.breakpoints.down('lg'));
    const mediaQuery = typeof fullScreen === 'string' ? fullScreen : '';
    const mediaQueryResult = useMediaQuery(mediaQuery);
    const applyFullScreen = typeof fullScreen === 'string' ? mediaQueryResult : fullScreen;

    const close = () => {
        setIsOpen(false);
        props.onClose?.();
    };

    const handleResponsiveDialogHeaderClose = () => {
        close();
    };

    const handleDialogClose = () => {
        if (allowClosing) {
            close();
        }
    };

    const renderHeader = () => {
        if (header) return header;

        if (props.title) {
            return (
                <ResponsiveDialogHeader
                    title={props.title}
                    onClose={allowClosing ? handleResponsiveDialogHeaderClose : undefined}
                />
            );
        }
        return null;
    };
       
    return (
        <Dialog
            open={isOpen}
            onClose={handleDialogClose}
            fullScreen={applyFullScreen}
            fullWidth={!applyFullScreen}
            maxWidth={false}
            slotProps={backdropBlur ? blurredBackdropSlotProps : undefined}
            className={cx('ResponsiveDialog', classes.responsiveDialogCSS, props.className, {
                fullScreen: applyFullScreen,
            })}
        >
            {renderHeader()}
            {props.children}
        </Dialog>
    );
};

export type UseResponsiveDialogProps = Omit<ResponsiveDialogProps, 'open'>;

export const useResponsiveDialog = (
    props: UseResponsiveDialogProps
): [dialog: ReactElement<any>, openDialog: (children: ReactNode) => void, closeDialog: () => void] => {
    const [children, setChildren] = useState<ReactNode>(props.children);
    const [isOpen, setIsOpen] = useState(false);

    const handleClose = () => {
        setIsOpen(false);
    };

    const dialog = (
        <ResponsiveDialog
            allowClosing={props.allowClosing}
            className={props.className}
            fullScreen={props.fullScreen}
            onClose={handleClose}
            open={isOpen}
            sizeSettings={props.sizeSettings}
            title={props.title}
        >
            {children}
        </ResponsiveDialog>
    );

    const open = (children: ReactNode) => {
        setChildren(children);
        setIsOpen(true);
    };

    const close = () => {
        setIsOpen(false);
    };

    return [dialog, open, close];
};
